import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, Observable, take, tap, throwError } from 'rxjs';

import { Group, ResourceType, User, UserSettings } from '@models';
import { Store } from '@ngrx/store';
import { logError } from '@shared/utils';
import { AppState, onSessionEnd } from '@store';
import { UserService } from './user.service';

export type MembershipListItem = Selectable<
  (Group | User) & { isIncluded: boolean }
>;

@Service()
export class ResourceMembershipsService {
  store = inject<Store<AppState>>(Store);
  userService = inject(UserService);

  allMemberships: MembershipListItem[] = [];
  isLoading = false;
  userBean!: UserSettings;

  constructor() {
    onSessionEnd(this.clear);
    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.userBean = state.user;
      });
  }

  getMembers = (type: number, subtype: number) => {
    const isGroups = type === ResourceType.USER;
    const externals = subtype > 2;

    const request: Observable<(Group | User)[]> = isGroups
      ? this.userService.getGroupsList({ fromLevel: 4 })
      : this.userService.getUsersList({ externals: externals ? 1 : 0 });

    return request.pipe(
      take(1),
      tap((responseData) => {
        this.allMemberships = responseData
          .map((member) => ({
            ...member,
            isIncluded: false,
            selected: false,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
      }),
      catchError((error) => {
        logError(error);

        return throwError(() => new Error('Error fetching members'));
      })
    );
  };

  get includedMemberships() {
    return this.allMemberships.filter((item) => item.isIncluded);
  }

  get excludedMemberships() {
    return this.allMemberships.filter((item) => !item.isIncluded);
  }

  get selectedMemberships() {
    return this.allMemberships.filter((item) => item.selected);
  }

  includeCurrentUser = () => {
    const selfMember = this.allMemberships.find(
      ({ rid }) => rid === this.userBean.userId
    );

    if (selfMember) {
      this.includeMembership(selfMember, true);
    }
  };

  updateMembership = <T extends MembershipListItem>(
    item: MembershipListItem,
    partial: Partial<T>
  ) => {
    this.allMemberships = this.allMemberships.map((member) =>
      member.rid === item.rid ? { ...member, ...partial } : member
    );
  };

  toggleMembershipSelection = (membership: MembershipListItem) => {
    this.updateMembership(membership, { selected: !membership.selected });
  };

  includeMembership = (
    membership: MembershipListItem,
    includeAllSelected?: boolean
  ) => {
    this.updateMembership(membership, { isIncluded: true, selected: false });

    if (includeAllSelected) {
      this.includeSelectedMemberships();
    }
  };

  excludeMembership = (membership: MembershipListItem) => {
    this.updateMembership(membership, { isIncluded: false, selected: false });
  };

  includeSelectedMemberships = () => {
    this.selectedMemberships.forEach((membership) => {
      this.includeMembership(membership);
    });
  };

  excludeSelectedMemberships = () => {
    this.selectedMemberships.forEach((membership) => {
      this.excludeMembership(membership);
    });
  };

  clear = () => {
    this.allMemberships = [];
  };
}
